/**
 * Proposal contract terms — the clauses of the agreement the firm negotiates per project.
 *
 * The agreement (api/_proposalTerms.js) is otherwise fixed text shared by every proposal. Today
 * exactly one clause is negotiable here: Article 3.6, the initial payment due on execution.
 *
 * Storage and rationale: database/244_proposal_contract_terms.sql.
 * Server-side read (the one the document actually prints from): api/_proposalContractTerms.js.
 */

/**
 * The number Article 3.6 has always carried. A proposal with no negotiated term prints this, so a
 * blank field is not a missing value — it is the standard agreement.
 *
 * KEEP IN SYNC with DEFAULT_INITIAL_PAYMENT_PCT in api/_proposalContractTerms.js — the deliberate
 * mirror this repo already uses for PROPOSAL_PHASES ↔ PHASE_SPEC and CS_SITE_VISITS, because the
 * server file cannot be imported into the browser bundle.
 */
export const DEFAULT_INITIAL_PAYMENT_PCT = 10

async function api(action, params = {}) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/proposal-contract-terms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ action, ...params }),
  }).then(r => r.json())
  if (res.error) throw new Error(res.error)
  return res
}

/**
 * The negotiated terms for one proposal.
 * Always resolved: `initialPaymentPct` is the firm's standard when nothing has been negotiated,
 * and `isCustom` is how the editor tells the two apart.
 */
export async function getContractTerms(proposalId) {
  if (!proposalId) return { initialPaymentPct: DEFAULT_INITIAL_PAYMENT_PCT, isCustom: false, note: '', allotmentScope: '' }
  const { terms } = await api('get', { proposalId })
  return terms
}

/** Record a negotiated initial payment. The server refuses anything outside 0-100. */
export async function saveContractTerms(proposalId, { initialPaymentPct, note }) {
  const { terms } = await api('save', { proposalId, initialPaymentPct, note })
  return terms
}

/** Return the agreement to its standard wording. */
export async function clearContractTerms(proposalId) {
  const { terms } = await api('clear', { proposalId })
  return terms
}

/**
 * Design Consultation: record what the allotment is intended to cover (Markdown, "- " bullets).
 * Blank clears it, and the agreement then prints no scope at all. database/285.
 */
export async function saveAllotmentScope(proposalId, allotmentScope) {
  const { terms } = await api('saveScope', { proposalId, allotmentScope })
  return terms
}

/**
 * Area metrics left out of the fee (database/388): { "<Fee Proposals field>": number }. Replaces
 * the whole map. While a metric is in here its Airtable field is blank (so the FPV ignores it) and
 * the proposal document prints this number in Exhibit A instead.
 */
export async function saveExcludedMetrics(proposalId, excludedMetrics) {
  const { terms } = await api('saveExcluded', { proposalId, excludedMetrics })
  return terms
}
